/* eslint-disable no-param-reassign */
import _last from 'lodash-es/last';
import React, { useEffect, useMemo, useState } from 'react';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  buildConfig,
  NodeData,
} from '@teetsh/app/src/containers/Sequences/List/Sidebar/utils';
import flatMapThatTree from './flatMapThatTree';
import wasPreviousNodeExpanded from './wasPreviousNodeExpanded';
import { Tree2 } from '@teetsh/app/src/components/Tree2';
import { ITreeNode } from '@teetsh/app/src/components/Tree2/types';
import { useUpdateSequencesFolders } from 'queries/sequences/useUpdateSequencesFolders';
import { DraggableOverlay } from './DraggableOverlay';
import { useUpdateSequenceOfFicheDePrep } from 'queries/fdp/useUpdateFicheDePrepSequence';
import { useMoveStandaloneFicheDePrep } from 'queries/fdp/useMoveStandaloneFicheDePrep';
import { useTranslation } from 'react-i18next';

interface Props {
  nodes: Array<ITreeNode>;
  path: string[];
  onClickFicheDePrep?: (ficheDePrepId: string, sequenceId?: string) => void;
  onClickSequence?: (
    sequenceId: string,
    sequenceName: string,
    parents: string[],
  ) => void;
  onClickFolder?: (
    folderId: string,
    folderName: string,
    parents: string[],
  ) => void;
}

export function Navigation(props: Props) {
  const { path } = props;
  const [nodes, setNodes] = useState<Array<ITreeNode>>(props.nodes);
  const updateSequenceOfFdp = useUpdateSequenceOfFicheDePrep();
  const [activeId, setActiveId] = useState<string | undefined>(undefined);
  const { t } = useTranslation(['sequence']);
  const config = buildConfig(t);
  const { mutateAsync: moveStandaloneFicheDePrep } =
    useMoveStandaloneFicheDePrep();
  const updateSequencePath = useUpdateSequencesFolders();
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
        tolerance: 10,
      },
    }),
  );
  const flattenOldNodes: ITreeNode<NodeData>[] = flatMapThatTree(props.nodes);
  const allNodesTitleConcat = flattenOldNodes.map(n => n.label).join(',');
  useEffect(() => {
    const flattenNodes: any[] = flatMapThatTree(nodes);
    const updateNodes = (nds: Array<ITreeNode>) =>
      nds.map(node => ({
        ...node,
        isExpanded:
          wasPreviousNodeExpanded(flattenNodes, node.id) ||
          path.includes(node.id as string),
        childNodes: updateNodes(node.childNodes || []),
      }));
    setNodes(updateNodes(props.nodes || []));
    return () => {};
  }, [allNodesTitleConcat, path.join(',')]);

  const handleNodeCollapse = (nodeData: ITreeNode) => {
    nodeData.isExpanded = false;
    setNodes([...nodes]);
  };

  const handleNodeExpand = (nodeData: ITreeNode) => {
    nodeData.isExpanded = true;
    setNodes([...nodes]);
  };

  const handleNodeClick = (node: ITreeNode<NodeData>) => {
    const type = node.nodeData?.type;
    const parents = node.nodeData?.parents || [];

    const { onClickFicheDePrep, onClickSequence, onClickFolder } = props;
    if (!type) {
      return;
    }
    switch (type) {
      case 'folder':
        node.isExpanded = !node.isExpanded;
        if (onClickFolder) {
          onClickFolder(node.id, node.label, parents);
        }
        return setNodes([...nodes]);

      case 'sequence':
        node.isExpanded = !node.isExpanded;
        if (onClickSequence) {
          onClickSequence(node.id, node.label, parents);
        }
        return setNodes([...nodes]);

      case 'fdp':
        if (onClickFicheDePrep) {
          const sequenceId = parents[parents.length - 1];
          onClickFicheDePrep(node.id, sequenceId);
        }
        return;

      case 'fdp_standalone':
        if (onClickFicheDePrep) {
          onClickFicheDePrep(node.id);
        }
        return;
    }
  };

  const flattenedItems = flatMapThatTree(nodes);

  const sortedIds = useMemo(
    () => flattenedItems.map(({ id }) => id),
    [flattenedItems],
  );

  function handleDragStart(event) {
    const { active } = event;

    setActiveId(active.id);
  }

  async function handleDragEnd({ active, over }: DragEndEvent) {
    setActiveId(undefined);
    if (!over) {
      return null;
    }
    if (active.id === over.id) {
      return null;
    }

    const overItem = flattenOldNodes.find(f => f.id === over.id);
    if (!overItem) {
      return null;
    }
    const activeItem = flattenOldNodes.find(f => f.id === active.id);
    if (!activeItem) {
      return null;
    }

    if (
      !activeItem.nodeData?.type ||
      !overItem.nodeData?.type ||
      !config[activeItem.nodeData.type].allowedTargetTypes.includes(
        overItem.nodeData.type,
      )
    ) {
      // if disabled is over then we should just skip
      return null;
    }

    // Prevent folder to be moved inside a children of itself
    if (overItem.nodeData?.parents.includes(activeItem.id)) {
      return null;
    }
    if (!activeItem.nodeData) {
      return null;
    }
    if (overItem.isPlaceholder) {
      if (activeItem.nodeData?.type === 'fdp_standalone') {
        await moveStandaloneFicheDePrep({
          ficheDePrepId: activeItem.id,
          targetSequenceId: undefined,
          targetFolderId: undefined, // set to undefined to move to the root folder
        });
        return;
      }
      await updateSequencePath({
        items: [
          {
            id: activeItem.id,
            destFolderId: overItem.placeholderId,
            type: activeItem?.nodeData?.type,
          },
        ],
      });
      return;
    }
    if (overItem.nodeData?.type === 'sequence') {
      if (activeItem.nodeData?.type === 'fdp_standalone') {
        await moveStandaloneFicheDePrep({
          ficheDePrepId: activeItem.id,
          targetSequenceId: overItem.id,
          targetFolderId: undefined,
        });
        return;
      }
      if (activeItem.nodeData?.type === 'fdp') {
        const sequenceId = overItem?.id;
        await updateSequenceOfFdp({
          srcSequenceId: _last(activeItem.nodeData.parents)!,
          destSequenceId: sequenceId,
          ficheDePrepId: activeItem.id,
        });
        return;
      }
    }
    if (overItem.nodeData?.type === 'folder') {
      if (activeItem.nodeData?.type === 'fdp_standalone') {
        await moveStandaloneFicheDePrep({
          ficheDePrepId: activeItem.id,
          targetSequenceId: undefined,
          targetFolderId: overItem.id,
        });
        return;
      }
      await updateSequencePath({
        items: [
          {
            id: activeItem.id,
            destFolderId: overItem.id === 'root' ? undefined : overItem.id,
            type: activeItem?.nodeData?.type,
          },
        ],
      });
    }
    return;
  }

  const activeItem = flattenedItems.find(e => e.id === activeId);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      onDragStart={handleDragStart}
    >
      <SortableContext items={sortedIds} strategy={verticalListSortingStrategy}>
        <Tree2
          nodes={nodes}
          onNodeClick={handleNodeClick}
          onNodeCollapse={handleNodeCollapse}
          onNodeExpand={handleNodeExpand}
          draggingItem={activeItem}
        />
      </SortableContext>
      <DraggableOverlay active={activeItem} />
    </DndContext>
  );
}
